import LoadingElement from "@shared/components/LoadingElement";
import AppConst from "@shared/entities/AppConsts";
import IEvent from "@shared/entities/RegisterEvent/IEvent";
import { isDev } from "@shared/Utils";
import axios, { AxiosResponse } from "axios";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

/** 寫在前端的Config */
export interface IFrontendConfig {
  /** 後端的API起始位置 */
  baseURL: string;
}

/** 寫在後端的Config */
export interface IBackendConfig {
  boat: {
    [eventKey: string]: {
      BoatReservationCloseTimestamp: number;
      BoatReservationOpenTimestamp: number;
      MaxBoatReservationPerUser: number;
    }
  }
  config: {
    recaptchaEnabled: boolean;
    recaptchaSiteKey: string;
    quizEnabled: boolean;
    needQuizFromTimestamp: number;
    needQuizToTimestamp: number;
  };
  form: {}
  fursuiter: {
    [eventKey: string]: {
      fursuiterTagLimit: number;
      changeRoomPrice: number;
    }
  }
  hotel: {
    [eventKey: string]: {
      dataEditDeadline: boolean;
      hotelOrderCloseTimestamp: number;
      hotelOrderOpenTimestamp: number;
      allowedAccomodationDays: number[];
    };
  };
  point: {}
  registration: {
    [eventKey: string]: {
      dataEditDeadline: number;
      registrationCloseTimestamp: number;
      registrationOpenTimestamp: number;
      /** 升級報名時的加收費用 */
      upgradeFeeFactor: number;

      /* */
      multiDateRegistrationUpgradeCloseTimestamp: number
      multiDateRegistrationUpgradeFeeFactor: number
      multiDateRegistrationUpgradeOpenTimestamp: number
      onsiteRegistrationToken: string

      afterPartyTicketOpenTimestamp: number
      afterPartyTicketCloseTimestamp: number

      donateAmountStep: number;
    };
  };
  user: {
    OAuthServices: string[]
  }
}

/** 處理Config的Service */
export interface IConfigService {
  /** 前端配置 */
  frontendConfig: IFrontendConfig;

  /** 後端配置 */
  backendConfig: IBackendConfig;

  /** 後端傳過來現在時間，用來判定是否可報名用 */
  time: number;

  /** 是否為報名期間 */
  isRegisterOpen: boolean;

  /** 是否為現場報名期間 */
  isOnSiteRegisterOpen: boolean;

  /** 是否為 After Party 開放期間 */
  isAfterPartyOpen: boolean;

  /** 是否為飯店開放期間 */
  isHotelOpen: boolean;

  /** 可選擇的飯店過夜日數 */
  hotelAccomodationDays: number[];

  /** 是否為獸之船開放期間 */
  isBoatOpen: boolean;

  /** 是否防止壞壞 */
  isQuizNeeded: boolean;

  /** 毛毛標籤上限 */
  fursuitTagLimit: number;

  /** 毛毛更衣室價格 */
  fursuitChangeRoomPrice: number;

  /** 獸之船每人可訂上限 */
  boatReservationMaxPerUser: number;

  /** 現場報名Token */
  onsiteRegistrationToken: string;

}
const Context = createContext<IConfigService>(null);

export function useConfigService() {
  const context = useContext(Context);
  if (!context) {
    throw new Error("useConfigService 必須在ConfigService之下層執行！");
  }
  return context;
}

export const ConfigService = ({ children }) => {
  const [frontendConfig, setFrontendConfig] = useState<IFrontendConfig>();
  const [backendConfig, setBackendConfig] = useState<IBackendConfig>();
  const [time, setTime] = useState<number>(0);
  const [error, setError] = useState<any>()

  // TODO: localMode為 網路連不上時，應顯示網路受限版的頁面（如日本登機PWA版）
  const [localMode, setLocalMode] = useState<boolean>()

  /** 是否可報名 */
  const isRegisterOpen = useMemo(() => {
    if (!backendConfig) {
      return false;
    }
    return (
      time >
      backendConfig.registration?.[AppConst.eventID]
        ?.registrationOpenTimestamp &&
      time <
      backendConfig.registration?.[AppConst.eventID]?.registrationCloseTimestamp
    );
  }, [backendConfig, time]);

  /** 現場報名是否開啟 */
  const isOnSiteRegisterOpen = useMemo(() => {
    if (!backendConfig) {
      return false;
    }
    return (
      time >
      backendConfig.registration?.[AppConst.eventID]
        ?.multiDateRegistrationUpgradeOpenTimestamp &&
      time <
      backendConfig.registration?.[AppConst.eventID]?.multiDateRegistrationUpgradeCloseTimestamp
    );
  }, [backendConfig, time]);

  /** 預先現場報名Token */
  const onsiteRegistrationToken = useMemo(() => {
    if (!backendConfig) {
      return;
    }
    return backendConfig.registration?.[AppConst.eventID]?.onsiteRegistrationToken;
  }, [backendConfig])

  /** 是否開放購買 After Party 票券 */
  const isAfterPartyOpen = useMemo(() => {
    if (!backendConfig) {
      return false;
    }
    return (
      time >
      backendConfig.registration?.[AppConst.eventID]?.afterPartyTicketOpenTimestamp &&
      time <
      backendConfig.registration?.[AppConst.eventID]?.afterPartyTicketCloseTimestamp
    )
  }, [backendConfig])


  /** 是否開放飯店報名 */
  const isHotelOpen = useMemo(() => {
    if (!backendConfig) {
      return false;
    }
    return (
      time >
      backendConfig.hotel?.[AppConst.eventID]
        ?.hotelOrderOpenTimestamp &&
      time <
      backendConfig.hotel?.[AppConst.eventID]?.hotelOrderCloseTimestamp
    );
  }, [backendConfig, time])
  /** 可選擇的飯店過夜日數 */
  const hotelAccomodationDays = useMemo(() => {
    if (!backendConfig) {
      return [];
    }

    return backendConfig.hotel?.[AppConst.eventID]?.allowedAccomodationDays || []

  }, [backendConfig])

  /** TODO:是否開放獸之船報名 */
  const isBoatOpen = useMemo(() => {
    if (!backendConfig) {
      return false;
    }
    return (
      time >
      backendConfig.boat?.[AppConst.eventID]
        ?.BoatReservationOpenTimestamp &&
      time <
      backendConfig.boat?.[AppConst.eventID]?.BoatReservationCloseTimestamp
    );
  }, [backendConfig, time])
  /** 是否需要加回答問題 */
  const isQuizNeeded = useMemo(() => {
    if (!backendConfig) {
      return false;
    }

    if (!backendConfig.config.quizEnabled) {
      return false;
    }

    return time > backendConfig.config.needQuizFromTimestamp &&
      time < backendConfig.config.needQuizToTimestamp;
  }, [time, backendConfig])

  /** 毛毛標籤上限 */
  const fursuitTagLimit = useMemo(() => {
    if (!backendConfig) {
      return 0;
    }

    return backendConfig.fursuiter?.[AppConst.eventID]?.fursuiterTagLimit || 0

  }, [backendConfig])

  /** 毛毛更衣室價格 */
  const fursuitChangeRoomPrice = useMemo(() => {
    if (!backendConfig) {
      return 0;
    }

    return backendConfig.fursuiter?.[AppConst.eventID]?.changeRoomPrice || 0

  }, [backendConfig])

  /** 獸之船每人可訂上限 */
  const boatReservationMaxPerUser = useMemo(() => {
    if (!backendConfig) {
      return 0;
    }
    return backendConfig.boat?.[AppConst.eventID]?.MaxBoatReservationPerUser || 1;
  }, [backendConfig])

  // 取前端的Config
  useEffect(() => {
    if (frontendConfig) {
      return;
    }
    axios
      .get("/assets/config.json", { baseURL: '' })
      .then((response: AxiosResponse<IFrontendConfig>) => {
        // 如果是測試機時, API位置鎖定在松氏機房 :tm:
        if (isDev()) {
          response.data.baseURL = AppConst.devAPIBase
        }
        response.data.baseURL = response.data.baseURL.endsWith("/")
          ? response.data.baseURL.substring(0, response.data.baseURL.length - 1)
          : response.data.baseURL;

        setFrontendConfig(response.data);
      })
      .catch(err => {
        console.log(err)
        setError(err)
        throw err;
      });
  }, [frontendConfig]);

  // 讀完前端配置檔之後才知道後端在哪
  useEffect(() => {
    if (!frontendConfig) {
      return;
    }
    axios.get(`${frontendConfig.baseURL}/config`).then(
      (
        response: AxiosResponse<{
          success: boolean;
          config: IBackendConfig;
          events: IEvent;
          time: number;
        }>
      ) => {
        if (response.data.success) {
          setBackendConfig(response.data.config);
          setTime(response.data.time);
        }
      }
    ).catch(err => {
      // 後端API取得失敗時, 設定為本地模式
      console.log(err)
      setError(err)
      setLocalMode(true)
      throw err;
    });
  }, [frontendConfig]);

  useEffect(() => {
    if (error) {
      setError(null)
      throw new Error('初始化中發生錯誤！')
    }
  }, [error])

  const context = useMemo(
    () => ({
      frontendConfig,
      backendConfig,
      time,
      isRegisterOpen,
      isOnSiteRegisterOpen,
      isAfterPartyOpen,
      isHotelOpen,
      hotelAccomodationDays,
      isBoatOpen,
      isQuizNeeded,
      fursuitTagLimit,
      fursuitChangeRoomPrice,
      boatReservationMaxPerUser,
      onsiteRegistrationToken
    }),
    [
      backendConfig, frontendConfig,
      isRegisterOpen, isOnSiteRegisterOpen,
      time, isAfterPartyOpen,
      isHotelOpen, isQuizNeeded, hotelAccomodationDays,
      isBoatOpen, boatReservationMaxPerUser,
      fursuitTagLimit, fursuitChangeRoomPrice,
      onsiteRegistrationToken
    ]
  );

  if (!frontendConfig || !backendConfig) {
    return <LoadingElement />;
  }
  return <Context.Provider value={context}>{children}</Context.Provider>;
};
